'use client';
import ProductBanner from '@components/ProductBanner';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import Image from '@repo/ui/src/components/Image';
import ProductPrice from '@components/Product/Price';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { classNames } from '@repo/ui/src/utils/classNames';
import { logProduct } from '@utils/productWrapper';
import CallToActionButton from '@components/Product/CallToActionButton';
import { Anchor } from '@repo/ui/src/components/Anchor';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useAddToBasket from '@hooks/basket/useAddToBasket';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import RatingStars from '@repo/ui/src/components/RatingStars';
import { normalize } from './ProductCard.helpers';
import { ProductProps } from '@repo/ui/src/types';

interface Props {
  product: ProductProps;
  _testID?: string;
  index?: number;
  listName?: string;
  newText?: string;
  saleText?: string;
  hideAddToCartButton?: boolean;
  isCarousel?: boolean;
  outOfStockText?: string;
  small?: boolean;
  showFinancing?: boolean;
  financingText?: string;
}

const ProductCard = ({
  product,
  _testID,
  index,
  listName,
  newText,
  saleText,
  hideAddToCartButton,
  isCarousel,
  outOfStockText,
  small = false,
  showFinancing = false,
  financingText = 'View Financing',
}: Props) => {
  const { locale, push } = useCurrentUrl();
  const { t } = useTranslation();
  const linkObj = buildLinkObj({ link: product?.deliveryKey });
  const { trigger, isMutating } = useAddToBasket();
  const { push: pushModal } = useModal(ModalID.error);

  const financingClicked = () => {
    if (!showFinancing) {
      return;
    }
    if (!window.affirm) {
      pushModal({
        title: t('popupBlockerModal.title', undefined, {
          default: `We're sorry!`,
        }),
        subtitle: t('popupBlockerModal.subtitle', undefined, {
          default: 'Something went wrong',
        }),
        detail: t('popupBlockerModal.detail', undefined, {
          default: `To view the financing information please disable your ad-blocker and refresh the page. If you're not using an ad-blocker please close this popup and try again.`,
        }),
        buttonText: t('popupBlockerModal.buttonText', undefined, {
          default: `Continue Shopping`,
        }),
      });
    } else {
      window.affirm?.ui.openModal({ amount: product.affirmPrice });
    }
  };

  const productClick = React.useCallback(
    async (e: any) => {
      e.preventDefault();

      await logProduct('PRODUCT_CLICKED', {
        product: product,
        list_name: listName,
        locale,
        position: index,
        coupon_name: product?.promotionId,
      });

      const linkHref = linkObj.href || product.productUrl;
      if (linkHref) push(linkHref);
    },
    [product, index, listName, linkObj, push],
  );

  if (!product) return null;

  const { title, image } = normalize(product);

  return (
    <div
      role="group"
      aria-label="product"
      data-test-id={`product_card_${_testID}${index}`}
      className={classNames(
        isCarousel && 'tw-border tw-border-warm-300',
        'tw-relative tw-h-full tw-z-10 tw-bg-white focus:tw-outline-none',
        // focus
        'focus:tw-outline-none focus:tw-outline-2 focus:tw-ring focus:tw-ring-brand-200',
        // after
        'after:tw-absolute after:-tw-z-20 after:-tw-inset-0 after:tw-transition after:tw-duration-300 after:tw-ease-in-out after:tw-bg-gradient-to-br after:tw-from-white after:tw-via-white after:tw-to-warm-100 after:tw-opacity-0',
        // hover
        'hover:after:tw-opacity-100',
      )}
    >
      <Anchor
        link={linkObj.href}
        onClick={productClick}
        className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full"
        tabIndex={-1}
      />
      <div
        className={classNames(
          'tw-grid tw-h-full tw-justify-items-center tw-text-center tw-content-end tw-grid-rows-[auto_1fr]',
          small ? 'tw-px-4 tw-pb-4 tw-gap-3 tw-pt-4' : 'tw-px-16 tw-pb-8 tw-gap-5',
        )}
        data-testid="pc-ribbon"
      >
        {!small && (
          <ProductBanner
            product={product}
            outOfStockText={outOfStockText}
            newText={newText}
            saleText={saleText}
            renderEmpty
          />
        )}
        <div
          className={classNames(
            small && 'tw-gap-1',
            'tw-grid tw-grid-rows-1 tw-gap-4 tw-justify-center',
          )}
        >
          <Anchor
            link={linkObj.href}
            onClick={productClick}
            className="tw-self-center tw-cursor-pointer"
            dataTestId="pc-link"
          >
            {title && (
              <h3
                className={classNames(
                  'tw-relative tra-text-web-heading-6 tw-mb-0',
                  small && 'tw-text-lg tw-leading-none',
                )}
                data-testid="pc-title"
              >
                {title}
              </h3>
            )}
          </Anchor>
          <div className="tw-self-end">
            <ProductPrice
              product={product}
              className="card-price tw-relative"
              align="center"
              variant={small && 'small'}
            />
            <div className="tw-flex tw-justify-center tw-py-4" data-testid="pc-review-container">
              <RatingStars value={product?.ratingSummary?.average} />
            </div>
          </div>
        </div>
        <div className={classNames('tw-relative -tw-z-10', small && 'tw-max-w-[100px]')}>
          {image && (
            <Image
              src={image}
              alt={title}
              height={200}
              width={200}
              quality={75}
              data-testid="pc-image"
              loading="eager"
              objectFit="cover"
            />
          )}
        </div>
        <div data-testid="pc-cta-container" className="tw-z-10">
          {!hideAddToCartButton && (
            <div
              className={classNames(
                showFinancing && 'tw-flex tw-flex-wrap tw-gap-4 tw-justify-center',
              )}
            >
              {showFinancing && (
                <Button
                  className="tw-rounded"
                  variant={ButtonVariant.secondary}
                  size="small"
                  onClick={financingClicked}
                >
                  {financingText}
                </Button>
              )}
              <CallToActionButton
                buttonProps={{
                  variant: ButtonVariant.secondary,
                  size: 'small',
                  disabled: isMutating,
                }}
                className="tw-rounded"
                onClickAddToCart={() => {
                  product.quantity = 1;
                  trigger([product]);
                }}
                product={product}
                buttonProcessing={isMutating}
                processingText=""
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
